import { useNavigate } from 'react-router-dom';

type PasswordModalProps = {
  data: {
    isOpen: boolean;
    step: 'current' | 'reset';
    password: string;
    newPassword: string;
    confirmPassword: string;
    showPassword: boolean;
    showNewPassword: boolean;
    showConfirmPassword: boolean;
    canProceedCurrent: boolean;
    canProceedReset: boolean;
    hasMinLength: boolean;
    hasLowercase: boolean;
    hasUppercase: boolean;
    hasNumber: boolean;
    hasSpecial: boolean;
    showRequirements: boolean;
    isLoading: boolean;
    errorMessage: string | null;
  };
  actions: {
    onClose: () => void;
    onNextFromCurrent: () => void;
    onFinishReset: () => void;
    onPasswordChange: (value: string) => void;
    onNewPasswordChange: (value: string) => void;
    onConfirmPasswordChange: (value: string) => void;
    onToggleShowPassword: () => void;
    onToggleShowNewPassword: () => void;
    onToggleShowConfirmPassword: () => void;
  };
};

const PasswordModal = ({ data, actions }: PasswordModalProps) => {
  const navigate = useNavigate();
  const {
    isOpen,
    step,
    password,
    newPassword,
    confirmPassword,
    showPassword,
    showNewPassword,
    showConfirmPassword,
    canProceedCurrent,
    canProceedReset,
    hasMinLength,
    hasLowercase,
    hasUppercase,
    hasNumber,
    hasSpecial,
    showRequirements,
    isLoading,
    errorMessage,
  } = data;
  const {
    onClose,
    onNextFromCurrent,
    onFinishReset,
    onPasswordChange,
    onNewPasswordChange,
    onConfirmPasswordChange,
    onToggleShowPassword,
    onToggleShowNewPassword,
    onToggleShowConfirmPassword,
  } = actions;
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-8">
      <div className="w-full max-w-[440px] rounded-[16px] border border-slate-200 bg-white p-6 shadow-xl">
        <div className="flex items-center justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white">
              <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="5" y="11" width="14" height="9" rx="2" />
                <path d="M8 11V8a4 4 0 1 1 8 0v3" />
              </svg>
            </div>
          </div>
        </div>
        <h3 className="mt-4 text-center text-lg font-semibold text-slate-900">
          {step === 'current' ? 'Change your Password' : 'Reset Password'}
        </h3>
        {step === 'current' ? (
          <>
            <div className="mt-4">
              <label className="text-sm font-semibold text-slate-700">
                Enter your Current Password
              </label>
              <div className="mt-2 flex items-center rounded-[12px] border border-slate-200 px-4 py-2">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => onPasswordChange(event.target.value)}
                  placeholder=""
                  className="flex-1 bg-transparent text-sm text-slate-700 outline-none"
                />
                <button
                  type="button"
                  onClick={onToggleShowPassword}
                  className="text-slate-500 transition hover:text-slate-700"
                  aria-label="Toggle password visibility"
                >
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </div>
              <button
                type="button"
                className="mt-2 w-full text-right text-sm font-semibold text-blue-600"
                onClick={() => navigate('/forgot-password', { state: { from: 'settings' } })}
              >
                Forgot Password?
              </button>
            </div>
            <div className="mt-6 flex items-center justify-between gap-3">
              <button
                type="button"
                className="flex-1 rounded-[12px] border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="flex-1 rounded-[12px] bg-blue-200 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70"
                disabled={!canProceedCurrent}
                onClick={onNextFromCurrent}
              >
                Next
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mt-4 flex flex-col gap-4">
              <div>
                <label className="text-sm font-semibold text-slate-700">
                  Enter New Password
                </label>
                <div className="mt-2 flex items-center rounded-[12px] border border-slate-200 px-4 py-2">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(event) => onNewPasswordChange(event.target.value)}
                    placeholder=""
                    className="flex-1 bg-transparent text-sm text-slate-700 outline-none"
                  />
                  <button
                    type="button"
                    onClick={onToggleShowNewPassword}
                    className="text-slate-500 transition hover:text-slate-700"
                    aria-label="Toggle new password visibility"
                  >
                    <svg
                      className="h-5 w-5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700">
                  Confirm New Password
                </label>
                <div className="mt-2 flex items-center rounded-[12px] border border-slate-200 px-4 py-2">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(event) => onConfirmPasswordChange(event.target.value)}
                    placeholder=""
                    className="flex-1 bg-transparent text-sm text-slate-700 outline-none"
                  />
                  <button
                    type="button"
                    onClick={onToggleShowConfirmPassword}
                    className="text-slate-500 transition hover:text-slate-700"
                    aria-label="Toggle confirm password visibility"
                  >
                    <svg
                      className="h-5 w-5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            {showRequirements ? (
              <div className="mt-4">
                <p className="text-sm font-semibold text-slate-700">
                  Password must have:
                </p>
                <div className="mt-2 space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                        hasMinLength
                          ? 'border-emerald-500 text-emerald-500'
                          : 'border-rose-500 text-rose-500'
                      }`}
                    >
                      {hasMinLength ? (
                        <svg
                          className="h-3 w-3"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="m5 12 4 4L19 6" />
                        </svg>
                      ) : (
                        'X'
                      )}
                    </span>
                    <span
                      className={
                        hasMinLength ? 'text-emerald-600' : 'text-rose-500'
                      }
                    >
                      At least 8 characters
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                        hasLowercase
                          ? 'border-emerald-500 text-emerald-500'
                          : 'border-rose-500 text-rose-500'
                      }`}
                    >
                      {hasLowercase ? (
                        <svg
                          className="h-3 w-3"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="m5 12 4 4L19 6" />
                        </svg>
                      ) : (
                        'X'
                      )}
                    </span>
                    <span
                      className={
                        hasLowercase ? 'text-emerald-600' : 'text-rose-500'
                      }
                    >
                      Lowercase character (a-z)
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                        hasUppercase
                          ? 'border-emerald-500 text-emerald-500'
                          : 'border-rose-500 text-rose-500'
                      }`}
                    >
                      {hasUppercase ? (
                        <svg
                          className="h-3 w-3"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="m5 12 4 4L19 6" />
                        </svg>
                      ) : (
                        'X'
                      )}
                    </span>
                    <span
                      className={
                        hasUppercase ? 'text-emerald-600' : 'text-rose-500'
                      }
                    >
                      Uppercase character (A-Z)
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                        hasSpecial
                          ? 'border-emerald-500 text-emerald-500'
                          : 'border-rose-500 text-rose-500'
                      }`}
                    >
                      {hasSpecial ? (
                        <svg
                          className="h-3 w-3"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="m5 12 4 4L19 6" />
                        </svg>
                      ) : (
                        'X'
                      )}
                    </span>
                    <span
                      className={
                        hasSpecial ? 'text-emerald-600' : 'text-rose-500'
                      }
                    >
                      Special character (!@#$%)
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                        hasNumber
                          ? 'border-emerald-500 text-emerald-500'
                          : 'border-rose-500 text-rose-500'
                      }`}
                    >
                      {hasNumber ? (
                        <svg
                          className="h-3 w-3"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="m5 12 4 4L19 6" />
                        </svg>
                      ) : (
                        'X'
                      )}
                    </span>
                    <span
                      className={
                        hasNumber ? 'text-emerald-600' : 'text-rose-500'
                      }
                    >
                      Number (0-9)
                    </span>
                  </div>
                </div>
              </div>
            ) : null}
            {errorMessage && (
              <div className="mt-4 rounded-lg bg-red-50 border border-red-200 p-3">
                <p className="text-sm text-red-600">{errorMessage}</p>
              </div>
            )}
            <div className="mt-6 flex items-center justify-between gap-3">
              <button
                type="button"
                className="flex-1 rounded-[12px] border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 disabled:opacity-50"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="flex-1 rounded-[12px] bg-blue-200 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70 flex items-center justify-center gap-2"
                disabled={!canProceedReset || isLoading}
                onClick={onFinishReset}
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>Changing...</span>
                  </>
                ) : (
                  'Next'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PasswordModal;
